/**
 * Thread Subscription Hooks (feature #62)
 * React Query hooks for following / unfollowing a message thread.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getSubscription,
  subscribeToThread,
  unsubscribeFromThread,
} from '@/services/thread-subscriptions.service';

/**
 * Whether the current user follows the thread rooted at `messageId`.
 */
export const useThreadSubscription = (messageId: string | null) => {
  return useQuery({
    queryKey: ['thread-subscription', messageId],
    queryFn: () => getSubscription(messageId as string),
    enabled: !!messageId,
    staleTime: 60 * 1000,
  });
};

/**
 * Toggle following a thread. Pass the current subscription state so the
 * mutation knows whether to subscribe or unsubscribe.
 */
export const useToggleThreadSubscription = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ messageId, subscribed }: { messageId: string; subscribed: boolean }) => {
      if (subscribed) {
        await unsubscribeFromThread(messageId);
        return false;
      }
      await subscribeToThread(messageId);
      return true;
    },
    onSuccess: (nowSubscribed, { messageId }) => {
      queryClient.setQueryData(['thread-subscription', messageId], nowSubscribed);
      toast.success(nowSubscribed ? 'Следиш темата' : 'Спря да следиш темата');
    },
    onError: (error) => {
      console.error('Failed to toggle thread subscription:', error);
      toast.error('Грешка при следене на темата');
    },
  });
};
